import React from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import BackgroundPattern from '../components/BackgroundPattern';
import Footer from '../components/Footer';

const Landing: React.FC = () => {
  usePageTitle('Home');
  const navigate = useNavigate();

  // Define brand color hex for consistency (manual overwrite)
  // Base: #e03761 (Raspberry)
  // Light tint for bg: #fce7ea
  // Medium tint for bor: #f8b8c4
  
  return (
    <div className="relative min-h-screen bg-[#fdf2f4] font-sans selection:bg-[#e03761]/20 selection:text-[#e03761]">
      {/* Global Pattern Overlay */}
      <BackgroundPattern />

      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold font-proyas tracking-wider text-white">PROYAS</h1>
          <button
            onClick={() => navigate('/login')}
            className="px-3 py-2 sm:px-4 sm:py-2 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm sm:text-base shadow-sm"
          >
            Log In
          </button>
        </div>
      </nav>

      {/* Red Header Background with Wave */}
      <div className="absolute top-0 left-0 right-0 h-[350px] bg-[#e03761] z-0 overflow-hidden">
        {/* Wave Pattern */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-auto text-[#fdf2f4] fill-current block translate-y-px" viewBox="0 0 1440 320" preserveAspectRatio="none">
             <path fillOpacity="0.3" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
             <path fillOpacity="0.5" d="M0,160L48,176C96,192,192,224,288,224C384,224,480,192,576,197.3C672,203,768,245,864,256C960,267,1056,245,1152,229.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
             <path fillOpacity="1" d="M0,224L48,234.7C96,245,192,267,288,261.3C384,256,480,224,576,202.7C672,181,768,171,864,181.3C960,192,1056,224,1152,224C1248,224,1344,192,1392,176L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
        
        {/* Decorative Circles */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white opacity-[0.05] rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white opacity-[0.05] rounded-full blur-3xl pointer-events-none translate-y-1/4 -translate-x-1/4"></div>
      </div>

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 lg:pt-48 lg:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mt-12 md:mt-20">
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#fce7ea] text-[#e03761] text-sm font-bold mb-8 shadow-sm animate-fade-in-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#e03761] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#be1843]"></span>
              </span>
              The #1 Verified Care Marketplace
            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight leading-[1.1] mb-8">
              Building a bridge between <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#e03761] to-[#be1843]">care and opportunity</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-12 max-w-3xl mx-auto font-medium">
              Proyas is the country's first verified digital marketplace for structured, transparent, and expert senior care.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto px-8 py-4 bg-[#e03761] text-white rounded-2xl font-bold text-lg hover:bg-[#be1843] transition-all transform hover:scale-105 shadow-xl shadow-[#e03761]/30 flex items-center justify-center gap-2 ring-4 ring-[#e03761]/10"
              >
                Get Started
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('how-it-works');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto px-8 py-4 bg-white text-[#e03761] border border-[#f8b8c4] rounded-2xl font-bold text-lg hover:bg-[#fce7ea] transition-colors shadow-sm hover:shadow-md"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="max-w-7xl mx-auto mt-20 md:mt-32">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="flex flex-col items-center text-center p-6 bg-white/60 backdrop-blur-sm border border-[#fce7ea] rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-14 h-14 bg-[#fce7ea] text-[#e03761] rounded-2xl flex items-center justify-center mb-4">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Verified Professionals</h3>
                <p className="text-gray-500">Every caregiver undergoes rigorous background checks and skill verification.</p>
              </div>
              
              <div className="flex flex-col items-center text-center p-6 bg-white/60 backdrop-blur-sm border border-[#fce7ea] rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-14 h-14 bg-[#fce7ea] text-[#e03761] rounded-2xl flex items-center justify-center mb-4">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Structured & Transparent</h3>
                <p className="text-gray-500">Clear pricing, defined roles, and seamless digital contracts.</p>
              </div>
              
              <div className="flex flex-col items-center text-center p-6 bg-white/60 backdrop-blur-sm border border-[#fce7ea] rounded-3xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-14 h-14 bg-[#fce7ea] text-[#e03761] rounded-2xl flex items-center justify-center mb-4">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Expert Senior Care</h3>
                <p className="text-gray-500">Specialized support tailored to the unique needs of the elderly.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Soft fade out at bottom to merge with next section */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#fdf2f4] to-transparent pointer-events-none"></div>
      </div>

      {/* Pathways Section */}
      <div id="how-it-works" className="py-24 px-6 relative overflow-hidden">
         {/* Decorative blob */}
         <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#f8b8c4] rounded-full blur-[120px] opacity-30 pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Path</h2>
            <p className="text-gray-500 text-lg">Whether you need care for a loved one or want to provide care, we have a place for you.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Customer Card */}
            <div className="group relative bg-white rounded-[2rem] p-8 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-[#fce7ea]">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#fce7ea] to-transparent rounded-full -mr-16 -mt-16 opacity-50 group-hover:scale-110 transition-transform duration-500"></div>
              
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-[#fce7ea] text-[#e03761] rounded-2xl flex items-center justify-center mb-8 shadow-sm">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Find Care</h3>
                <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                  Connect with verified medical professionals and compassionate assistants for your loved ones. Peace of mind is just a click away.
                </p>
                
                <button
                  onClick={() => navigate('/register')}
                  className="inline-flex items-center text-[#e03761] font-bold hover:text-[#be1843] transition-colors group-hover:translate-x-1 duration-300 text-lg"
                >
                  Find a Caregiver
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            </div>

            {/* Caregiver Card */}
            <div className="group relative bg-[#e03761] rounded-[2rem] p-8 shadow-xl shadow-[#e03761]/20 hover:shadow-2xl hover:shadow-[#e03761]/30 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-16 -mt-16 opacity-50 group-hover:scale-110 transition-transform duration-500"></div>
              
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm text-white rounded-2xl flex items-center justify-center mb-8">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-4">Provide Care</h3>
                <p className="text-white/90 text-lg mb-8 leading-relaxed">
                  Join the country's first verified marketplace. Build your reputation, manage your schedule, and earn with dignity and transparency.
                </p>
                
                <button
                  onClick={() => navigate('/register')}
                  className="inline-flex items-center text-white font-bold hover:text-white/80 transition-colors group-hover:translate-x-1 duration-300 text-lg"
                >
                  Start Caregiving
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Breakdown */}
      <div className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-3 py-1 rounded-full bg-[#fce7ea] text-[#e03761] text-xs font-bold tracking-wide uppercase mb-4">Our Services</div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Expert care for every need</h2>
            <p className="text-gray-500 text-lg leading-relaxed">
              We understand that every individual has unique requirements. That's why we've structured our services to provide exactly the right level of support.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center gap-4 group p-8 rounded-[2rem] bg-white border border-[#fce7ea] shadow-sm hover:shadow-xl hover:shadow-[#e03761]/5 transition-all duration-300">
              <div className="w-16 h-16 shrink-0 bg-[#fce7ea] text-[#e03761] rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 group-hover:text-[#e03761] transition-colors mb-4">Nurse Care</h3>
                <p className="text-gray-500 text-lg leading-relaxed">For ongoing medical needs, post-operative care, and health monitoring by certified professionals.</p>
              </div>
            </div>
            
            <div className="flex flex-col items-center text-center gap-4 group p-8 rounded-[2rem] bg-white border border-[#fce7ea] shadow-sm hover:shadow-xl hover:shadow-[#e03761]/5 transition-all duration-300">
              <div className="w-16 h-16 shrink-0 bg-[#fce7ea] text-[#e03761] rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 group-hover:text-[#e03761] transition-colors mb-4">Everyday Assistance</h3>
                <p className="text-gray-500 text-lg leading-relaxed">Support with daily living activities, mobility, meal preparation, and companionship.</p>
              </div>
            </div>
          </div>

          {/* Tech/App Feature Integration */}
          <div className="mt-32 pt-24">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              
              {/* Visual Side: App Mockup/Notification Stack */}
              <div className="relative order-2 md:order-1 flex justify-center">
                {/* Background Glow */}
                <div className="absolute inset-0 bg-[#e03761] rounded-full blur-[100px] opacity-10 transform scale-75"></div>
                
                <div className="relative w-full max-w-sm space-y-4">
                  {/* Notification 1 */}
                  <div className="bg-white p-4 rounded-2xl shadow-xl shadow-[#e03761]/10 border border-[#fce7ea] transform -rotate-2 hover:rotate-0 transition-transform duration-300 cursor-default">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#e03761] text-white rounded-full flex items-center justify-center shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">shohAI</p>
                            <p className="text-xs text-gray-500">"I've found 3 caregivers matching your needs."</p>
                        </div>
                      </div>
                  </div>

                  {/* Notification 2 */}
                  <div className="bg-white p-4 rounded-2xl shadow-xl shadow-[#e03761]/10 border border-[#fce7ea] transform translate-x-4 hover:translate-x-2 transition-transform duration-300 cursor-default">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Safety Check Passed</p>
                            <p className="text-xs text-gray-500">Routine active status verified</p>
                        </div>
                      </div>
                  </div>

                  {/* Notification 3 */}
                  <div className="bg-white p-4 rounded-2xl shadow-xl shadow-[#e03761]/10 border border-[#fce7ea] transform rotate-2 hover:rotate-0 transition-transform duration-300 cursor-default">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-yellow-50 text-yellow-500 rounded-full flex items-center justify-center shrink-0">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Review Received</p>
                            <p className="text-xs text-gray-500">5.0 ★ from your caregiver</p>
                        </div>
                      </div>
                  </div>
                </div>
              </div>

              {/* Text Side */}
              <div className="order-1 md:order-2">
                <div className="inline-block px-3 py-1 rounded-full bg-[#fce7ea] text-[#e03761] text-xs font-bold tracking-wide uppercase mb-4">Smart Platform</div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">More than just a connection</h2>
                <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                  We provide the tools to ensure transparency and safety. Effectively communicate, rate experiences, and keep everyone accountable.
                </p>
                
                <ul className="space-y-4">
                  {[
                      'shohAI: AI assistant for generic & personalized care guidance',
                      'Both-way rating & review system',
                      'Secure internal contact terminal',
                      'Automated safety check system',
                      'Detailed patient context for caregivers',
                      'Real-time status notifications'
                  ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-700 font-medium">
                        <div className="w-6 h-6 rounded-full bg-[#fce7ea] text-[#e03761] flex items-center justify-center shrink-0">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        </div>
                        {item}
                      </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-6 bg-[#e03761] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" style={{ 
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px' 
        }}></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight drop-shadow-sm">
            Ready to experience <br/> better senior care?
          </h2>
          <p className="text-[#fce7ea] text-xl mb-10 max-w-2xl mx-auto">
            Join the Proyas community today. Whether you're seeking care or providing it, we're here to support you every step of the way.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-10 py-4 bg-white text-[#e03761] rounded-full font-bold text-lg hover:bg-[#fdf2f4] transition-all transform hover:scale-105 shadow-2xl hover:shadow-white/20"
          >
            Sign Up Now
          </button>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
